import React, { useState } from "react";
import { Grid, MenuItem } from "@mui/material";
import {
  Field,
  FieldLabel,
  FieldValue,
  FieldWrapper,
  TimeControlHeader,
  TimeControlTitle,
} from "../styles";
import { useTranslation } from "react-i18next";
import { getBreakTime, getTotalTime, getTransitTime, getWorkTime } from "Util/timeControl";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { format, isBefore, parseISO } from "date-fns";
import { Task } from "Models/task";
import { Input } from "Components/Form";
import { SplitButton } from "Components/Form/SplitButton";
import { Color } from "Components/Form/Button";
import { useExpenseHooks } from "./hooks/useExpenseHooks";

interface TimeControlProps {
  task: Task | null;
  setTask: React.Dispatch<React.SetStateAction<Task | null>>;
  isLoading?: boolean;
  onSave?: (task: Task) => void; // New prop for triggering ExpenseManagement refresh
  refetchExpenses?: () => void;
  isUserEnableToEdit?: boolean;
}

/**
 * Expected chronological sequence of time fields
 */
const timeFieldsSequence: (keyof Task)[] = [
  "leavingWarehouse",
  "arriveResidence",
  "breakTake",
  "breakBack",
  "leavingResidence",
  "arriveBackWarehouse",
];

/**
 * Processes datetime-local inputs to store in UTC format
 */
const handleDateTimeInput = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
): string => {
  const inputValue = event?.target?.value;

  if (!inputValue) return "";

  try {
    // The datetime-local format is 'YYYY-MM-DDThh:mm'
    const localDate = new Date(inputValue);

    if (isNaN(localDate.getTime())) {
      return "";
    }

    // Convert to UTC ISO string for storage
    const utcDate = zonedTimeToUtc(
      localDate,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ).toISOString();

    return utcDate;
  } catch (err) {
    console.error("Error processing date:", err);
    return "";
  }
};

/**
 * Formats ISO date for display in datetime-local input
 */
const formatDateForInput = (isoDateString: string): string => {
  if (!isoDateString) return "";

  try {
    // Convert from UTC to local timezone
    const localDate = utcToZonedTime(
      isoDateString,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    // Format as YYYY-MM-DDThh:mm (format accepted by datetime-local input)
    return format(localDate, "yyyy-MM-dd'T'HH:mm");
  } catch (err) {
    console.error("Error formatting date:", err);
    return "";
  }
};

/**
 * Checks if there is a value in an ISO date
 */
const hasValue = (dateStr: string | undefined): boolean => {
  return !!dateStr && dateStr !== "";
};

/**
 * Validates the time sequence and returns a corrected task if necessary
 */
const validateTimeSequence = (task: Task): Task => {
  if (!task) return task;

  const newTask: Record<string, any> = { ...task };
  let changedOccurred = false;

  // Check the time sequence and clear inconsistent fields
  for (let i = 0; i < timeFieldsSequence.length; i++) {
    const currentField = timeFieldsSequence[i];

    // Skip if the current field has no value
    if (!hasValue(newTask[currentField] as string)) continue;

    // Check if it is in sequence with the previous field
    if (i > 0) {
      const prevField = timeFieldsSequence[i - 1];

      if (
        hasValue(newTask[prevField] as string) &&
        isBefore(parseISO(newTask[currentField] as string), parseISO(newTask[prevField] as string))
      ) {
        // Clear the current field if it is before the previous field
        newTask[currentField] = "" as any;
        changedOccurred = true;

        // Clear all subsequent fields as well
        for (let j = i + 1; j < timeFieldsSequence.length; j++) {
          if (hasValue(newTask[timeFieldsSequence[j]] as string)) {
            newTask[timeFieldsSequence[j]] = "";
            changedOccurred = true;
          }
        }
        break;
      }
    }

    // Check subsequent fields to ensure sequence
    for (let j = i + 1; j < timeFieldsSequence.length; j++) {
      const nextField = timeFieldsSequence[j];

      if (
        hasValue(newTask[nextField] as string) &&
        isBefore(parseISO(newTask[nextField] as string), parseISO(newTask[currentField] as string))
      ) {
        // If a later field is before the current field, clear all from that point
        for (let k = j; k < timeFieldsSequence.length; k++) {
          if (hasValue(newTask[timeFieldsSequence[k]] as string)) {
            newTask[timeFieldsSequence[k]] = "" as any;
            changedOccurred = true;
          }
        }
        break;
      }
    }
  }

  return changedOccurred ? (newTask as Task) : task;
};

export function TimeControlForm({
  task,
  setTask,
  isLoading,
  onSave,
  refetchExpenses,
  isUserEnableToEdit = true,
}: TimeControlProps) {
  const { t: translate } = useTranslation();
  const [isEditMode, setEditMode] = useState(false);

  const { recalculateExpenses } = useExpenseHooks();
  const { mutate, isLoading: recalculatingExpenses } = recalculateExpenses(refetchExpenses);

  /**
   * Updates a specific field of the task object
   * and validates the time sequence immediately
   */
  const updateTaskField = (fieldName: keyof Task, value: string) => {
    if (!task) return;

    // Create a new task object with the updated value
    const updatedTask = {
      ...task,
      [fieldName]: value,
    } as Task;

    // Update the state with the validated task
    setTask(updatedTask);
  };

  const handleBlurTimeFields = () => {
    setTask((currentTask) => {
      if (!currentTask) return currentTask;

      // Immediately validate the time sequence with the new value
      return validateTimeSequence(currentTask);
    });
  };

  /**
   * Handles save button click
   */
  const handleSave = () => {
    setEditMode(false);
    // Call the onSave callback to trigger ExpenseManagement refresh
    if (onSave && task) {
      onSave(task);
    }
  };

  return (
    <Grid container direction="row">
      <Grid item xs={12} lg={12}>
        <TimeControlHeader>
          <TimeControlTitle>{translate("timeControl")}</TimeControlTitle>

          {isUserEnableToEdit && (
            <div>
              <SplitButton
                buttonLoading={isLoading}
                loading={recalculatingExpenses}
                onClick={() => (isEditMode ? handleSave() : setEditMode(true))}
                customColor={Color.BRAND}
                options={
                  <>
                    <MenuItem onClick={() => mutate(task?.id!)}>
                      {translate("recalculateExpenses")}
                    </MenuItem>
                  </>
                }
              >
                {isEditMode ? translate("save") : translate("edit")}
              </SplitButton>
            </div>
          )}
        </TimeControlHeader>
      </Grid>
      <Grid item container direction="row" xs={12} lg={12}>
        <Grid item xs={12} md={3} lg={3}>
          <Field>
            <FieldLabel>{translate("transitTime")}</FieldLabel>
            <FieldValue>
              {getTransitTime({
                leavingResidence: task?.leavingResidence || "",
                arriveResidence: task?.arriveResidence || "",
                leavingNextJob: task?.leavingNextJob || "",
                leavingWarehouse: task?.leavingWarehouse || "",
                arriveBackWarehouse: task?.arriveBackWarehouse || "",
              } as Task) || "00:00"}
            </FieldValue>
          </Field>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Field>
            <FieldLabel>{translate("breakTime")}</FieldLabel>
            <FieldValue>
              {getBreakTime({
                breakBack: task?.breakBack || "",
                breakTake: task?.breakTake || "",
              } as Task) || "00:00"}
            </FieldValue>
          </Field>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Field>
            <FieldLabel>{translate("workTime")}</FieldLabel>
            <FieldValue>
              {getWorkTime({
                arriveResidence: task?.arriveResidence || "",
                leavingResidence: task?.leavingResidence || "",
              } as Task) || "00:00"}
            </FieldValue>
          </Field>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Field>
            <FieldLabel>{translate("totalTime")}</FieldLabel>
            <FieldValue>
              {getTotalTime({
                arriveResidence: task?.arriveResidence || "",
                breakTake: task?.breakTake || "",
                breakBack: task?.breakBack || "",
                leavingResidence: task?.leavingResidence || "",
                arriveBackWarehouse: task?.arriveBackWarehouse || "",
                leavingWarehouse: task?.leavingWarehouse || "",
                leavingNextJob: task?.leavingNextJob || "",
              } as Task) || "00:00"}
            </FieldValue>
          </Field>
        </Grid>
      </Grid>

      <FieldWrapper>
        <Input
          disabled={!isEditMode}
          label={translate("leavingTheWarehouse")}
          placeholder="00:00"
          type="datetime-local"
          value={formatDateForInput(task?.leavingWarehouse || "")}
          onChange={(event) => updateTaskField("leavingWarehouse", handleDateTimeInput(event))}
        />
        <Input
          disabled={!isEditMode}
          label={translate("arrivedAtTheResidence")}
          placeholder="00:00"
          type="datetime-local"
          value={formatDateForInput(task?.arriveResidence || "")}
          onChange={(event) => updateTaskField("arriveResidence", handleDateTimeInput(event))}
          min={formatDateForInput(task?.leavingWarehouse || "")}
          onBlur={handleBlurTimeFields}
        />
        <Input
          disabled={!isEditMode}
          label={translate("breakFrom")}
          placeholder="00:00"
          type="datetime-local"
          value={formatDateForInput(task?.breakTake || "")}
          onChange={(event) => updateTaskField("breakTake", handleDateTimeInput(event))}
          onBlur={handleBlurTimeFields}
          min={formatDateForInput(task?.arriveResidence || "")}
        />
        <Input
          disabled={!isEditMode}
          label={translate("backFromBreak")}
          placeholder="00:00"
          type="datetime-local"
          value={formatDateForInput(task?.breakBack || "")}
          onChange={(event) => updateTaskField("breakBack", handleDateTimeInput(event))}
          onBlur={handleBlurTimeFields}
          min={formatDateForInput(task?.breakTake || "")}
        />
        <Input
          disabled={!isEditMode}
          label={translate("leavingTheResidence")}
          placeholder="00:00"
          type="datetime-local"
          value={formatDateForInput(task?.leavingResidence || "")}
          onChange={(event) => updateTaskField("leavingResidence", handleDateTimeInput(event))}
          onBlur={handleBlurTimeFields}
          min={formatDateForInput(task?.breakBack || task?.arriveResidence || "")}
        />
        <Input
          disabled={!isEditMode}
          label={translate("arrivedBackToTheWarehouse")}
          placeholder="00:00"
          type="datetime-local"
          value={formatDateForInput(task?.arriveBackWarehouse || "")}
          onChange={(event) => updateTaskField("arriveBackWarehouse", handleDateTimeInput(event))}
          onBlur={handleBlurTimeFields}
          min={formatDateForInput(task?.leavingResidence || "")}
        />
      </FieldWrapper>
    </Grid>
  );
}
